import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Save, User } from "lucide-react";
import medicalDB from "../services/database";

const PatientForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);

  // Utility function to convert Arabic numerals to regular numbers
  const convertArabicNumerals = (value) => {
    if (!value) return value;
    const arabicToEnglish = {
      "٠": "0",
      "١": "1",
      "٢": "2",
      "٣": "3",
      "٤": "4",
      "٥": "5",
      "٦": "6",
      "٧": "7",
      "٨": "8",
      "٩": "9",
    };
    return value
      .toString()
      .replace(/[٠-٩]/g, (match) => arabicToEnglish[match]);
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();

  useEffect(() => {
    loadLocations();
    if (isEditing) {
      loadPatient();
    }
  }, [id]);

  const loadLocations = async () => {
    try {
      const uniqueLocations = await medicalDB.getUniqueLocations();
      setLocations(uniqueLocations);
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };

  const loadPatient = async () => {
    try {
      setLoading(true);
      const data = await medicalDB.getPatient(id);
      if (data) {
        setPatient(data);
        // Populate form with existing data
        setValue("name", data.name);
        setValue("age", data.age);
        setValue("gender", data.gender);
        setValue("status", data.status);
        setValue("phone", data.phone || "");
        setValue("location", data.location || "");
        setValue("bloodType", data.bloodType || "");
      }
    } catch (error) {
      console.error("Error loading patient:", error);
      navigate("/patients");
    } finally {
      setLoading(false);
    }
  };

  const handleLocationInput = (e) => {
    const value = e.target.value;
    if (value.trim() === "") {
      setFilteredLocations([]);
      setShowLocationSuggestions(false);
    } else {
      const filtered = locations.filter((loc) =>
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredLocations(filtered);
      setShowLocationSuggestions(filtered.length > 0);
    }
  };

  const selectLocation = (location) => {
    setValue("location", location);
    setShowLocationSuggestions(false);
  };

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await medicalDB.updatePatient(id, data);
      } else {
        await medicalDB.createPatient(data);
      }
      navigate("/patients");
    } catch (error) {
      console.error("Error saving patient:", error);
      alert("Failed to save patient. Please try again.");
    }
  };

  if (isEditing && loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading patient...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/patients")}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? "Edit Patient" : "Add New Patient"}
          </h1>
          <p className="text-gray-600">
            {isEditing
              ? "Update patient information"
              : "Enter basic patient demographics"}
          </p>
        </div>
      </div>

      {/* Patient Form */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="card space-y-6"
        data-testid="patient-form"
      >
        <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
          <div className="w-12 h-12 bg-medical-lightblue rounded-full flex items-center justify-center">
            <User className="h-6 w-6 text-medical-blue" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Patient Information
            </h2>
            <p className="text-sm text-gray-600">Basic demographic details</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div className="md:col-span-2">
            <label className="form-label">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`form-input ${errors.name ? "border-red-500" : ""}`}
              placeholder="Enter patient's full name"
              data-testid="patient-name"
              {...register("name", {
                required: "Patient name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              })}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Age */}
          <div>
            <label className="form-label">
              Age <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              inputMode="numeric"
              className={`form-input ${errors.age ? "border-red-500" : ""}`}
              placeholder="Age in years"
              data-testid="patient-age"
              {...register("age", {
                required: "Age is required",
                validate: (value) => {
                  // Handle empty value
                  if (!value || value.toString().trim() === "") {
                    return "Age is required";
                  }

                  // Convert Arabic numerals before validation
                  const convertedValue = convertArabicNumerals(value);

                  // Check if the converted value contains only digits
                  if (!/^\d+(\.\d+)?$/.test(convertedValue)) {
                    return "Please enter a valid age using numbers only";
                  }

                  const numValue = parseFloat(convertedValue);

                  if (isNaN(numValue)) {
                    return "Please enter a valid number";
                  }
                  if (numValue < 0) {
                    return "Age must be a positive number";
                  }
                  if (numValue > 150) {
                    return "Please enter a valid age (0-150)";
                  }
                  if (numValue !== Math.floor(numValue)) {
                    return "Age must be a whole number";
                  }
                  return true;
                },
                setValueAs: (value) => {
                  // Convert Arabic numerals to English numerals when setting the value
                  if (!value) return value;
                  const converted = convertArabicNumerals(value);
                  const numValue = parseFloat(converted);
                  return isNaN(numValue) ? value : numValue;
                },
              })}
            />
            {errors.age && (
              <p className="mt-1 text-sm text-red-600">{errors.age.message}</p>
            )}
          </div>

          {/* Gender */}
          <div>
            <label className="form-label">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              className={`form-input ${errors.gender ? "border-red-500" : ""}`}
              data-testid="patient-gender"
              {...register("gender", {
                required: "Gender is required",
              })}
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            {errors.gender && (
              <p className="mt-1 text-sm text-red-600">
                {errors.gender.message}
              </p>
            )}
          </div>

          {/* Blood Type */}
          <div>
            <label className="form-label">Blood Type</label>
            <select
              className="form-input"
              data-testid="patient-blood-type"
              {...register("bloodType")}
            >
              <option value="">Select blood type</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
            <p className="mt-1 text-sm text-gray-600">
              Optional - Patient's blood type
            </p>
          </div>

          {/* Phone Number */}
          <div>
            <label className="form-label">Phone Number</label>
            <input
              type="tel"
              className="form-input"
              placeholder="Enter phone number"
              data-testid="patient-phone"
              {...register("phone")}
            />
            <p className="mt-1 text-sm text-gray-600">
              Optional - Contact phone number
            </p>
          </div>

          {/* Location */}
          <div className="relative">
            <label className="form-label">Location</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter location (city/area)"
              data-testid="patient-location"
              {...register("location")}
              onInput={handleLocationInput}
              onFocus={(e) => {
                if (e.target.value.trim() !== "") {
                  handleLocationInput(e);
                }
              }}
              onBlur={() => {
                // Delay hiding to allow click on suggestions
                setTimeout(() => setShowLocationSuggestions(false), 200);
              }}
              autoComplete="off"
            />
            {showLocationSuggestions && filteredLocations.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredLocations.map((location, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => selectLocation(location)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                  >
                    {location}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-1 text-sm text-gray-600">
              Optional - Patient's location or city
            </p>
          </div>

          {/* Status */}
          <div className="md:col-span-2">
            <label className="form-label">Status</label>
            <select className="form-input" {...register("status")}>
              <option value="Under Observation">Under Observation</option>
              <option value="Follow-Up">Follow-Up</option>
              <option value="Discharged">Discharged</option>
            </select>
            <p className="mt-1 text-sm text-gray-600">
              Current status of the patient in your care
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate("/patients")}
            className="btn-secondary"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary flex items-center space-x-2"
            data-testid="submit-patient"
          >
            <Save size={20} />
            <span>
              {isSubmitting
                ? "Saving..."
                : isEditing
                ? "Update Patient"
                : "Create Patient"}
            </span>
          </button>
        </div>
      </form>

      {/* Next Steps */}
      {!isEditing && (
        <div className="card bg-medical-lightblue border-l-4 border-medical-blue">
          <h3 className="font-medium text-medical-blue mb-2">Next Steps</h3>
          <p className="text-sm text-gray-700">
            After creating the patient record, you'll be able to add visit
            details, medical history, and manage their ongoing care.
          </p>
        </div>
      )}
    </div>
  );
};

export default PatientForm;
